// src/pages/Blog/BlogList.tsx
import React, { useMemo, useState } from 'react';
import Page from '../../../components/Page/Page';
import styled from '@emotion/styled';
import BlogListCard from './BlogListCard';
import { ChevronDoubleDownLight } from '../../../components/Icons/ChevronDown';
import { useGetBlogPosts } from '../../../hooks/blog/useGetBlogPosts';
import { Post } from '../BlogData';

const BlogList: React.FC = () => {
  const [showAll, setShowAll] = useState(false);

  const {
    posts,
    loading,
    error,
  }: {
    posts: Post[];
    loading: boolean;
    error: string | null;
    refresh: () => void;
  } = useGetBlogPosts();

  // sort newest-first and slice based on `showAll`
  const sortedPosts = useMemo(
    () =>
      [...posts].sort(
        (a, b) =>
          new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
      ),
    [posts]
  );
  const postsToShow = showAll ? sortedPosts : sortedPosts.slice(0, 3);

  if (loading) return <Full>Fetching…</Full>;
  if (error) return <Full>Error: {error}</Full>;

  return (
    <Page title="Locumspace | Blog – All Posts">
      <List>
        <h1>Welcome to Locumspace Blog - Stay Informed!</h1>
        {postsToShow.length > 0 ? (
          <div>
            {postsToShow.map((post) => (
              <BlogListCard key={post.id /* or index */} post={post} />
            ))}
          </div>
        ) : (
          <Soon>No Blog Posts at the moment, please check back later...</Soon>
        )}
      </List>

      {/* <div>
        <Soon>coming soon...</Soon>
      </div> */}

      {sortedPosts.length > 3 && (
        <ToggleContainer>
          <ToggleButton onClick={() => setShowAll((prev) => !prev)}>
            {showAll ? 'View Less' : 'View More'}
            <FlippableP flipped={showAll}>
              <ChevronDoubleDownLight />
            </FlippableP>
          </ToggleButton>
        </ToggleContainer>
      )}
    </Page>
  );
};

export default BlogList;

const List = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & > h1 {
    font-size: 2rem;
    font-weight: 600;
    margin-bottom: 2rem;
    text-align: center;
    color: ${({ theme }) => theme.palette.blackBlackMain};
  }

  & > div {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 2rem;
    padding: 2rem;

    /* cap at 3 columns by constraining container width */
    max-width: calc(400px * 3 + 2rem * 2);
    margin: 0 auto;
  }

  @media (max-width: 880px) {
    & > div {
      max-width: calc(100vw * 1 + 2rem * 2);
    }
  }

  @media (max-width: 480px) {
    & > p {
      font-size: 1.5rem;
    }

    & > div {
      max-width: calc(100vw * 1 + 2rem * 2);
    }
  }
`;

const ToggleContainer = styled.div`
  text-align: center;
  margin: 1rem 0 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.palette.mainBlue};
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const FlippableP = styled.p<{ flipped: boolean }>`
  display: inline-block;
  cursor: pointer;
  transition: transform 0.4s ease-in-out;
  transform: ${({ flipped }) => (flipped ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const Full = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 7rem;
`;

const Soon = styled.span`
  text-align: center;
  border: 1px solid red;
`;
